import { useEffect, useRef } from "react"
import { useLocation, useNavigationType } from "react-router-dom"

const SCROLL_STORAGE_KEY_PREFIX = "scroll_positions_"
const INITIAL_SCROLL_POSITION = 0
const THROTTLE_MS = 50
const SMOOTH_SCROLL_GUARD_MS = 250
const SCROLL_RESTORATION_THRESHOLD_MS = 200

const getTabId = (): string => {
	let tabId = sessionStorage.getItem("tab_id")
	if (!tabId) {
		tabId = crypto.randomUUID()
		sessionStorage.setItem("tab_id", tabId)
	}
	return tabId
}

const getStorageKey = () => `${SCROLL_STORAGE_KEY_PREFIX}${getTabId()}`

const scrollToPosition = (element: HTMLElement, position: number) => {
	element.scrollTo({
		top: position,
		behavior: "smooth",
	})
}

const parseStoredPositions = (key: string): Record<string, number> => {
	try {
		return JSON.parse(sessionStorage.getItem(key) ?? "{}")
	} catch {
		return {}
	}
}

const saveScrollPosition = (pathname: string, position: number) => {
	const key = getStorageKey()
	const positions = parseStoredPositions(key)
	positions[pathname] = position
	sessionStorage.setItem(key, JSON.stringify(positions))
}

export const useScrollRestoration = (isReadyForRestoration: boolean) => {
	const navType = useNavigationType()
	const { pathname, state } = useLocation()
	const pathnameRef = useRef(pathname)
	pathnameRef.current = pathname

	const isRestoringScroll = useRef(false)

	useEffect(() => {
		let lastSave = 0

		const handleScroll = () => {
			if (isRestoringScroll.current) return

			const now = Date.now()
			if (now - lastSave < THROTTLE_MS) return
			lastSave = now

			saveScrollPosition(
				pathnameRef.current,
				document.documentElement.scrollTop,
			)
		}

		window.addEventListener("scroll", handleScroll, { passive: true })
		return () => window.removeEventListener("scroll", handleScroll)
	}, [])

	useEffect(() => {
		if (!isReadyForRestoration) return

		const effectiveNavType = state?.simulatedType || navType

		if (effectiveNavType === "PUSH") {
			saveScrollPosition(pathnameRef.current, INITIAL_SCROLL_POSITION)
			scrollToPosition(document.documentElement, INITIAL_SCROLL_POSITION)
			return
		}

		const positions: Record<string, number> =
			parseStoredPositions(getStorageKey())
		const savedPosition = positions[pathname] ?? INITIAL_SCROLL_POSITION

		if (savedPosition <= 0) {
			scrollToPosition(document.documentElement, INITIAL_SCROLL_POSITION)
			return
		}

		isRestoringScroll.current = true

		let smoothScrollTimeout: unknown

		const timeoutId = setTimeout(() => {
			scrollToPosition(document.documentElement, savedPosition)

			smoothScrollTimeout = setTimeout(() => {
				isRestoringScroll.current = false
			}, SMOOTH_SCROLL_GUARD_MS)
		}, SCROLL_RESTORATION_THRESHOLD_MS)

		return () => {
			clearTimeout(timeoutId)
			clearTimeout(smoothScrollTimeout as number)
			isRestoringScroll.current = false
		}
	}, [pathname, isReadyForRestoration])
}
